import { Observable, createMutationObservable, isHTMLElement } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { Phase, onPhase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectTargetingSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';

declare var window: Window & yieldlove.Window & {
    googletag: googletag.Googletag;
};

const log = logger.logGroup({ prefix: 'CanonicalUrlCollector' });

const isHTMLLinkElement = (x: unknown): x is HTMLLinkElement => isHTMLElement(x) && x.tagName === 'LINK';
const isCanonicalLinkElement = (x: unknown): x is HTMLLinkElement => isHTMLLinkElement(x) && x.rel === 'canonical';

const canonicalUrl$ = new Observable<string>((destination) => {
    // initial search for canonical link element
    const element = document.head.querySelector('link[rel="canonical"]');
    if (isCanonicalLinkElement(element)) destination.next(element.href);

    // create a MutationObserver to monitor changes to canonical link element
    createMutationObservable(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] })
        .flatMap(mutations => mutations)
        .subscribe((mutation) => {
            if (mutation.type === 'childList') {
                // check if one of added nodes is a canonical link element
                for (const addedNode of Array.from(mutation.addedNodes)) {
                    if (isCanonicalLinkElement(addedNode)) destination.next(addedNode.href);
                }
                // what to do with removed nodes?
                // for (const removedNode of Array.from(mutation.removedNodes)) { }
            } else if (mutation.type === 'attributes' && mutation.attributeName === 'href' && isCanonicalLinkElement(mutation.target)) {
                destination.next(mutation.target.href);
            }
        }, { signal: destination.signal });
});

@injectionTarget()
export class CanonicalUrlCollector {

    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;

    @onPhase({ phase: Phase.TARGETING })
    public collectCanonicalUrl(): void {
        const { collectCanonicalUrl } = this.#configResolver.get(selectTargetingSettings()) ?? {};
        // if its disabled in the config, we do not need to get the canonicalUrl
        if (!collectCanonicalUrl) {
            log.debug('collectCanonicalUrl disabled in config');
            return;
        }

        canonicalUrl$.subscribe((pageUrl) => {
            log.debug(`canonicalUrl is ${pageUrl}`);

            // send to google
            window.googletag = window.googletag || {};
            window.googletag.cmd = window.googletag.cmd || [];
            window.googletag.cmd.push(() => {
                window.googletag.pubads().set('page_url', pageUrl);
            });

            // send to prebid
            (window.pbjsYLHH as any) ||= {};
            window.pbjsYLHH.cmd ||= [];
            window.pbjsYLHH.cmd.push(() => {
                window.pbjsYLHH.mergeConfig({ pageUrl });
            });
        });
    }
}
